<!-- Adding / Editing rules modal -->

<template>
  <b-modal
    v-model="showModal"
    id="pricingModal"
    title="Unit Pricing Rule"
    modal-class="pricing-modal"
    centered
    hide-footer
    size="lg"
  >
    <spinner-loader v-if="loading" />
    <div class="pricing-modal__content" @click="closeAllDatePicker">
      <b-form class="pricing-rate-form">
        <b-row>
          <b-col sm="12">
            <info-row
              class="info-main-row pricing-unit-ids py-2"
              title="Unit Name"
              :sizing="2"
            >
              <vue-multiselect
                v-model="form.unit_ids"
                :options="unitList"
                :multiple="true"
                :hide-selected="true"
                :allow-empty="false"
                :disabled="!!form.id"
                :searchable="false"
                label="name"
                track-by="id"
              />
            </info-row>
          </b-col>
        </b-row>

        <b-row>
          <b-col sm="6">
            <info-row class="info-main-row py-2" title="Name" :sizing="4">
              <b-form-input
                v-model="form.name"
                class="form-control"
                required
                :state="fieldState.name"
              />
            </info-row>
          </b-col>
          <b-col sm="6">
            <info-row class="info-main-row py-2" title="Rate" :sizing="4">
              <currency-input
                name="rate"
                :size="12"
                :amount.sync="form.rate"
                :field-state="fieldState.rate"
              />
            </info-row>
          </b-col>
        </b-row>

        <b-row>
          <b-col sm="12">
            <info-row
              class="info-main-row py-2"
              title="Description"
              :sizing="2"
            >
              <b-form-textarea
                v-model="form.description"
                class="form-control"
                required
              />
            </info-row>
          </b-col>
        </b-row>

        <b-row>
          <b-col sm="12" @click="preventClick">
            <info-row class="info-main-row py-2" title="Date Range" :sizing="2">
              <b-input-group
                class="pricing-date-range"
                @click="showDatePicker = !showDatePicker"
              >
                <b-form-input
                  :value="formattedDateRange"
                  :state="fieldState.starts_at"
                />
                <b-input-group-append is-text @click="preventClick">
                  <i
                    class="fi fi-calendar"
                    @click="showDatePicker = !showDatePicker"
                  />
                </b-input-group-append>
              </b-input-group>

              <div @click="preventClick">
                <v-date-picker
                  v-if="showDatePicker"
                  :value="dateRange"
                  :attributes="attrs"
                  :disabled-dates="disabledDates"
                  :disabled-attribute="disabledAttributes"
                  :show-day-popover="false"
                  class="pricing-date-picker inline"
                  mode="range"
                  is-inline
                  show-caps
                  @input="onSelectDateRange"
                />
              </div>
            </info-row>
          </b-col>
        </b-row>

        <b-row>
          <!-- <b-col sm="6" class="rule-color-picker">
            <info-row class="info-main-row py-2" title="Color" :sizing="4">
              <b-form-input
                v-model="form.color"
                class="form-control"
                readonly
                required
              />
              <div class="color-picker-container">
                <span class="current-color" :style="'background-color: ' + form.color" @click="onToggleColorPicker"></span>
                <sketch-picker
                  v-if="showColorPicker"
                  disableAlpha
                  :value="form.color || '#FFFFFF'"
                  @input="onSelectColor"
                />
              </div>
            </info-row>
          </b-col> -->
          <b-col sm="6">
            <info-row class="info-main-row py-2" title="Apply To" :sizing="4">
              <b-form-select
                v-model="form.repeat_type"
                id="repeat_type"
                class="form-control"
                required
                :options="repeatOptions"
                :state="fieldState.repeat_type"
                @change="onSelectRepeatType"
              />

              <b-popover target="repeat_type" triggers="hover" placement="top">
                {{ tooltips[form.repeat_type] }}
              </b-popover>
            </info-row>
          </b-col>
        </b-row>

        <b-row v-if="form.repeat_type > 0">
          <b-col sm="12" @click="preventClick">
            <info-row class="info-main-row py-2" title="Occurs" :sizing="2">
              <div v-if="form.repeat_type < 4">
                <b-input-group
                  class="pricing-repeat-items"
                  @click="showRepeatItems = !showRepeatItems"
                >
                  <b-form-input
                    class="form-control pricing-repeat-on"
                    :value="selectedRepeatLabels"
                    :state="fieldState.repeat_on"
                  />
                  <b-input-group-append is-text @click="preventClick">
                    <i
                      class="fi fi-calendar"
                      @click="showRepeatItems = !showRepeatItems"
                    />
                  </b-input-group-append>
                </b-input-group>

                <div class="repeat-on-wrapper" @click="preventClick">
                  <div v-show="showRepeatItems" class="repeat-on-selector">
                    <div
                      v-for="item in repeatOnItems[form.repeat_type]"
                      :key="item.value"
                      :id="`pricing-repeat-on=${item.value}`"
                      :class="[
                        'repeat-on-selector__content',
                        {
                          'repeat-on-selector__content--disabled':
                            item.disabled,
                        },
                        repeatOnClasses[form.repeat_type],
                      ]"
                      @click="onSelectRepeatOn(item)"
                    >
                      <span
                        :class="{ selected: isSelected(item.value) !== -1 }"
                        >{{ item.text }}</span
                      >

                      <b-popover
                        v-if="item.disabled"
                        :target="`pricing-repeat-on=${item.value}`"
                        triggers="hover"
                        placement="bottom"
                      >
                        Not with in date range
                      </b-popover>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="form.repeat_type === 4" @click="preventClick">
                <b-input-group
                  class="pricing-repeat-items"
                  @click="showRepeatItems = false"
                >
                  <b-form-input
                    class="form-control pricing-repeat-on"
                    :value="selectedRepeatLabels"
                    :state="fieldState.repeat_on"
                  />
                  <b-input-group-append is-text @click="preventClick">
                    <i
                      class="fi fi-calendar"
                      @click="showRepeatItems = !showRepeatItems"
                    />
                  </b-input-group-append>
                </b-input-group>

                <v-date-picker
                  v-if="showRepeatItems"
                  :value="selectedDates"
                  :attributes="attrs"
                  :show-day-popover="false"
                  class="pricing-date-picker inline"
                  mode="multiple"
                  is-inline
                  show-caps
                  @input="onSelectRepeatDates"
                />
              </div>
            </info-row>
          </b-col>
        </b-row>

        <b-row>
          <b-col sm="12">
            <info-row class="info-main-row py-2" title="Rate Type" :sizing="2">
              <b-form-select
                v-model="form.priority"
                id="rate_type"
                class="form-control"
                required
                :options="rateTypes"
                :state="fieldState.priority"
                @change="onSelectRepeatType"
              />
            </info-row>
          </b-col>
        </b-row>
      </b-form>

      <b-row class="mx-0 mt-2">
        <b-col :xs="form.id ? '4' : '6'" class="px-2">
          <b-button class="my-2 w-100" variant="success" @click="saveRule">
            Save
          </b-button>
        </b-col>
        <b-col v-if="form.id" xs="4" class="px-2">
          <b-button
            id="btn-rule-delete"
            variant="danger"
            :class="[
              'my-2 w-100',
              { 'pricing-rule-btn--disabled': form.is_used },
            ]"
            @click="confirmDelete"
          >
            Delete
          </b-button>
          <b-tooltip
            v-if="form.is_used"
            target="btn-rule-delete"
            placement="top"
          >
            This pricing rule was used in the past, so can not delete!
          </b-tooltip>
        </b-col>
        <b-col :xs="form.id ? '4' : '6'" class="px-2">
          <b-button class="my-2 w-100" variant="secondary" @click="hide">
            Close
          </b-button>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from "vuex";
import { Sketch } from "vue-color";
import AlertMixin from "@/mixins/alert-mixin";
import PricingMixin from "@/mixins/pricing-mixin";

export default {
  name: "PricingModal",

  components: {
    CurrencyInput: () => import("@/components/widgets/CurrencyInput"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
    InfoRow: () => import("@/components/InfoRow"),
    SketchPicker: Sketch,
  },

  mixins: [AlertMixin, PricingMixin],

  data() {
    return {
      attrs: [
        {
          key: "today",
          highlight: {
            borderColor: "#28a745",
            borderWidth: "2px",
            borderStyle: "solid",
          },
          dates: new Date(),
        },
      ],
      disabledAttributes: {
        contentStyle: {
          color: "#d3d3d3",
          cursor: "not-allowed",
          fontWeight: "400",
        },
      },
      dateRange: null,
      form: {
        id: 0,
        rate: 0,
        unit_id: 0,
        unit_ids: [],
        name: "",
        description: "",
        starts_at: null,
        ends_at: null,
        repeat_type: null,
        repeat_on: "",
        color: "",
        priority: "",
      },
      repeatOnClasses: {
        1: "repeat-on-selector__content--day",
        2: "repeat-on-selector__content--week",
        3: "repeat-on-selector__content--month",
      },
      checkingForm: false,
      loading: false,
      selectedRepeatItems: [],
      selectedRepeatLabels: "",
      selectedDates: [],
      showDatePicker: false,
      showColorPicker: false,
      showRepeatItems: false,
      showModal: false,
    };
  },

  computed: {
    fieldState() {
      return {
        name: this.checkingForm ? !!this.form.name : null,
        rate: this.checkingForm ? !!this.form.rate && this.form.rate > 0 : null,
        starts_at: this.checkingForm ? !!this.form.starts_at : null,
        ends_at: this.checkingForm
          ? !!this.form.ends_at && this.form.starts_at <= this.form.ends_at
          : null,
        repeat_type: this.checkingForm
          ? this.form.repeat_type !== null && this.form.repeat_type >= 0
          : null,
        repeat_on:
          this.checkingForm && this.form.repeat_type > 0
            ? !!this.form.repeat_on
            : null,
        priority: this.checkingForm ? !!this.form.priority : null,
      };
    },

    formattedDateRange() {
      let starts_at = this.form.starts_at
        ? this.formatDate(this.form.starts_at, "MMMM DD, YYYY")
        : "";

      let ends_at = this.form.ends_at
        ? this.formatDate(this.form.ends_at, "MMMM DD, YYYY")
        : "";

      return starts_at && ends_at ? `${starts_at} - ${ends_at}` : "";
    },

    disabledDates() {
      let last_date = this.$moment().subtract(1, "day");

      return {
        start: null,
        end: last_date,
      };
    },

    repeatOnItems() {
      let valid_days = [];
      let valid_dates = [];
      let valid_months = [];

      if (this.form.starts_at && this.form.ends_at) {
        let starts_at = this.$moment(this.form.starts_at);
        let ends_at = this.$moment(this.form.ends_at);

        let diff_days = ends_at.diff(starts_at, "days") + 1;
        let diff_months = ends_at.diff(starts_at, "months") + 1;

        valid_days = new Array(diff_days)
          .fill(0)
          .slice(0, 7)
          .map((_, i) => (starts_at.weekday() + i) % 7);

        valid_dates = new Array(diff_days)
          .fill(0)
          .slice(0, 31)
          .map((_, i) => ((starts_at.date() + i - 1) % 31) + 1);

        valid_months = new Array(diff_months)
          .fill(0)
          .slice(0, 12)
          .map((_, i) => ((starts_at.month() + i) % 12) + 1);
      }

      return {
        1: this.dateOptions.map((item) => {
          item.disabled = valid_dates.indexOf(parseInt(item.value)) === -1;
          return item;
        }),
        2: this.weekOptions.map((item) => {
          item.disabled = valid_days.indexOf(parseInt(item.value)) === -1;
          return item;
        }),
        3: this.monthOptions.map((item) => {
          item.disabled = valid_months.indexOf(parseInt(item.value)) === -1;
          return item;
        }),
      };
    },
  },

  methods: {
    ...mapActions([
      "addUnitPricingRule",
      "updateUnitPricingRule",
      "deleteUnitPricingRule",
    ]),

    init() {
      this.form = {
        id: 0,
        rate: 0,
        unit_id: 0,
        unit_ids: [],
        name: "",
        description: "",
        starts_at: null,
        ends_at: null,
        repeat_type: 0,
        repeat_on: "",
        color: "",
        priority: "",
      };

      this.checkingForm = false;
      this.dateRange = null;
      this.selectedRepeatItems = [];
      this.selectedRepeatLabels = "";
      this.selectedDates = [];
      this.showDatePicker = false;
      this.showColorPicker = false;
      this.showRepeatItems = false;
    },

    filterSelectedRepeatItems() {
      if (!this.form.repeat_type) {
        return;
      }

      this.selectedRepeatItems = this.selectedRepeatItems.filter((item) => {
        let repeat_item = this.repeatOnItems[this.form.repeat_type].find(
          (item_) => item_.value === item
        );

        return repeat_item && !repeat_item.disabled;
      });

      this.form.repeat_on = this.selectedRepeatItems.join(", ");
      this.selectedRepeatLabels = this.getRepeatOnLabels(
        this.form.repeat_type,
        this.form.repeat_on
      );
    },

    isSelected(value) {
      return this.selectedRepeatItems.findIndex((item) => item === value);
    },

    show(unit_id, rule = null) {
      this.init();

      if (rule) {
        this.form = rule;
        this.selectedRepeatItems = rule.repeat_on
          ? rule.repeat_on.split(", ")
          : [];

        let year = this.$moment().format("YYYY");

        if (rule.repeat_type === 4) {
          this.selectedDates = this.selectedRepeatItems
            .map((item) =>
              this.$moment(`${year}-${item}`).format("YYYY-MM-DD 00:00:00")
            )
            .map((item) => new Date(item));
        } else {
          this.selectedRepeatLabels = this.getRepeatOnLabels(
            rule.repeat_type,
            rule.repeat_on
          );
        }

        this.dateRange = {
          start: new Date(rule.starts_at),
          end: new Date(rule.ends_at),
        };
      }

      let unit = this.unitList.find((unit_) => unit_.id === unit_id);

      this.form.unit_ids = [unit];

      this.showModal = true;
    },

    hide() {
      this.showModal = false;
    },

    closeAllDatePicker() {
      this.showColorPicker = false;
      this.showDatePicker = false;
      this.showRepeatItems = false;
    },

    preventClick(e) {
      e.stopPropagation();
    },

    onToggleColorPicker(e) {
      e.preventDefault();
      this.showColorPicker = !this.showColorPicker;
    },

    onSelectColor(color) {
      this.form.color = color.hex;
    },

    onSelectDateRange(dates) {
      this.form.starts_at = dates.start;
      this.form.ends_at = dates.end;

      this.dateRange = dates;
      this.showDatePicker = false;
      this.filterSelectedRepeatItems();
    },

    onSelectRepeatDates(e) {
      this.selectedDates = e || [];

      this.selectedRepeatItems = this.selectedDates.map((date) =>
        this.$moment(date).format("MM-DD")
      );

      this.form.repeat_on = this.selectedRepeatItems.join(", ");
      this.selectedRepeatLabels = this.getRepeatOnLabels(
        this.form.repeat_type,
        this.form.repeat_on
      );
    },

    onSelectRepeatType(e) {
      this.selectedRepeatItems = [];
      this.selectedDates = [];
      this.selectedRepeatLabels = "";
      this.form.repeat_on = "";
    },

    onSelectRepeatOn(item) {
      if (item.disabled) {
        return;
      }

      let value = item.value;
      let index = this.isSelected(value);

      if (index >= 0) {
        this.selectedRepeatItems = [
          ...this.selectedRepeatItems.slice(0, index),
          ...this.selectedRepeatItems.slice(index + 1),
        ].sort((a, b) => b - a);
      } else {
        this.selectedRepeatItems.push(value);
      }

      this.form.repeat_on = this.selectedRepeatItems
        .sort((a, b) => a - b)
        .join(", ");

      this.selectedRepeatLabels = this.getRepeatOnLabels(
        this.form.repeat_type,
        this.form.repeat_on
      );
    },

    confirmDelete() {
      if (this.form.is_used) {
        return;
      }

      this.showConfirm("Are you sure?", "info").then((result) => {
        if (result.value) {
          this.deletePricingRule(this.form);
        }
      });
    },

    saveRule() {
      this.checkingForm = true;

      if (
        this.fieldState.name === false ||
        this.fieldState.rate === false ||
        this.fieldState.starts_at === false ||
        this.fieldState.ends_at === false ||
        this.fieldState.repeat_type === false ||
        this.fieldState.repeat_on === false ||
        this.fieldState.priority === false
      ) {
        return;
      }

      let params = Object.assign({}, this.form);

      params.starts_at = this.$moment(params.starts_at).format("YYYY-MM-DD");
      params.ends_at = this.$moment(params.ends_at).format("YYYY-MM-DD");

      if (this.form.id) {
        delete params.unit_ids;
        this.updatePricingRule(params);
      } else {
        params.unit_ids = params.unit_ids.map((unit) => unit.id);
        this.addPricingRule(params);
      }
    },

    addPricingRule(params) {
      this.loading = true;

      this.addUnitPricingRule(params)
        .then(() => {
          this.successNotification(
            "New pricing rule have been successfully added"
          );
          this.$emit("updated");
          this.hide();
        })
        .catch((err) => {
          this.errorNotification("Failed to add new pricing rule");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updatePricingRule(params) {
      this.loading = true;

      this.updateUnitPricingRule(params)
        .then(() => {
          this.successNotification(
            "Pricing rule have been successfully updated"
          );
          this.$emit("updated");
          this.hide();
        })
        .catch((err) => {
          this.errorNotification("Failed to update pricing rule");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deletePricingRule(item) {
      this.loading = true;

      this.deleteUnitPricingRule(item)
        .then(() => {
          this.successNotification(
            "Pricing rule have been successfully deleted"
          );
          this.$emit("updated");
          this.hide();
        })
        .catch((err) => {
          this.errorNotification("Failed to delete pricing rule");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.pricing-modal {
  .modal-dialog {
    max-width: 620px;
    width: 90%;
    margin: 1rem auto;

    @media (max-width: 768px) {
      width: 95%;
      margin: 0.5rem auto;
    }
  }

  &__content {
    position: relative;
  }

  .v-spinner {
    z-index: 999;
  }

  .info-main-row {
    @media (max-width: 576px) {
      display: block !important;
      padding-top: 4px !important;
      padding-bottom: 2px !important;

      .entry-label {
        width: 100%;
        max-width: 100%;
        flex: 0 0 100%;
        text-align: left;
        // margin-bottom: 2px;
        height: auto;

        .entry-label-text {
          position: static;
          left: auto;
          top: auto;
        }
      }

      .entry-info {
        width: 100%;
        max-width: 100%;
        flex: 0 0 100%;
        line-height: 1.5;
        // margin-bottom: 8px;
      }
    }
  }

  .pricing-date-picker {
    &.inline {
      bottom: 48px;
      position: absolute;
      z-index: 2;
      width: 100%;
      max-width: 300px;

      @media (max-width: 576px) {
        max-width: 100%;
        left: 0;
      }

      &:before {
        content: "";
        border: 8px solid #dadada;
        border-left-color: transparent;
        border-right-color: transparent;
        position: absolute;
        border-bottom-color: transparent;
        right: 120px;
        bottom: -16px;
        z-index: 1;
      }

      &:after {
        content: "";
        border: 8px solid rgb(250, 250, 250);
        border-left-color: transparent;
        border-right-color: transparent;
        position: absolute;
        border-bottom-color: transparent;
        right: 120px;
        bottom: -15px;
        z-index: 2;
      }
    }

    input {
      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      font-weight: 400;
      line-height: 1.8;
      max-width: 100%;
      padding: 4px 12px;
      pointer-events: none;
      width: 100%;
    }

    &.error {
      input {
        border-color: #dc3545 !important;
      }
    }
  }

  .pricing-date-range {
    input {
      max-width: 100%;
      pointer-events: none;
    }

    .input-group-text {
      padding: 4px 6px;
    }

    .fi-calendar {
      cursor: pointer;
      font-size: 26px;
      line-height: 1;
    }
  }

  .pricing-rate-form {
    padding: 0px 10px 0px 15px;

    @media (max-width: 576px) {
      padding: 0;
    }

    .row {
      @media (max-width: 576px) {
        margin-bottom: 0.5rem;
      }
    }

    .col-sm-6 {
      @media (max-width: 576px) {
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 0.5rem;
      }
    }

    .bill-field {
      padding: 0 !important;
    }

    .currency-input {
      min-width: 158px;

      @media (max-width: 576px) {
        min-width: auto;
        width: 100%;
      }
    }
  }

  .pricing-unit-ids {
    .multiselect__tags {
      border: 1px solid #ced4da;
      min-height: 20px;
      line-height: 1;
    }
  }

  .pricing-repeat-items {
    .input-group-text {
      padding: 4px 6px;
    }

    .fi-calendar {
      cursor: pointer;
      font-size: 26px;
      line-height: 1;
    }
  }

  .pricing-rule-btn {
    &--disabled {
      opacity: 0.65;
      cursor: default;
    }
  }

  .pricing-repeat-on {
    pointer-events: none;
  }

  .rule-color-picker {
    .color-picker-container {
      position: absolute;
      right: 15px;
      top: 0px;
      bottom: 0;
      padding: 3px;
      background-color: #eee;
      border: 1px solid #ccc;
      border-radius: 4px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;

      .current-color {
        background-color: #000;
        cursor: pointer;
        display: inline-block;
        width: 30px;
        height: 30px;
      }

      .vc-sketch {
        position: absolute;
        right: 0;
        bottom: 50px;
        z-index: 99;
      }
    }
  }

  .repeat-on-wrapper {
    position: absolute;
    left: 137px;
    bottom: 38px;
    padding-bottom: 10px;

    @media (max-width: 576px) {
      left: 0;
      width: 100%;
    }

    .repeat-on-selector {
      background-color: rgb(250, 250, 250);
      border: 1px solid rgb(218, 218, 218);
      border-radius: 5px;
      color: #393d46;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-start;
      font-size: 14.4px;
      font-weight: 400;
      line-height: 1.5;
      width: 256px;

      @media (max-width: 576px) {
        width: 100%;
      }

      &:before {
        content: "";
        border: 8px solid #dadada;
        border-left-color: transparent;
        border-right-color: transparent;
        position: absolute;
        border-bottom-color: transparent;
        right: 120px;
        bottom: -5px;
        z-index: 1;
      }

      &:after {
        content: "";
        border: 8px solid rgb(250, 250, 250);
        border-left-color: transparent;
        border-right-color: transparent;
        position: absolute;
        border-bottom-color: transparent;
        right: 120px;
        bottom: -4px;
        z-index: 2;
      }

      &__content {
        cursor: pointer;
        padding: 4px;
        line-height: 2;
        text-align: center;

        & > span {
          border-radius: 5px;
          display: inline-block;
          width: 100%;

          &:hover {
            background-color: rgb(218, 218, 218);
          }

          &.selected {
            background-color: rgb(102, 179, 204);
            color: rgb(250, 250, 250);
          }
        }

        &--day {
          flex: 0 0 14.28%;
          max-width: 14.28%;

          @media (max-width: 576px) {
            flex: 0 0 25%;
            max-width: 25%;
          }

          & > span {
            border-radius: 50%;
          }
        }

        &--week {
          flex: 0 0 25%;
          max-width: 25%;

          @media (max-width: 576px) {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }

        &--month {
          flex: 0 0 33.33%;
          max-width: 33.33%;

          @media (max-width: 576px) {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }

        &--disabled {
          color: #d3d3d3;
          cursor: not-allowed;

          & > span {
            &:hover {
              background-color: transparent;
            }
          }
        }
      }
    }
  }

  .mx-0 {
    @media (max-width: 576px) {
      flex-direction: column;

      .col {
        width: 100%;
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 0.5rem;
        padding: 0 0.5rem !important;

        &:last-child {
          margin-bottom: 0;
        }

        .btn {
          width: 100%;
        }
      }
    }
  }
}
</style>
